import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import {
    ArrowLeft, ArrowRight, Check, Globe, FileText, BarChart3,
    Clock, MapPin, Send, Loader2, ExternalLink,
    Zap, Calendar, Repeat, Image as ImageIcon, Plus, X, Upload,
    Monitor, Smartphone, CalendarDays, Battery,
    ChevronLeft, ChevronRight,
} from 'lucide-react';
import {
    getPushDomains, createPushCampaign, getPushTags, createPushTag, getTranslationLanguages,
    getPushGroups,
} from '@/services/api/push';
import { getMedia, uploadMedia, getMediaUrl, getThumbnailUrl } from '@/services/api';
import type { MediaItem } from '@/services/api/media';
import {PushTag, PushCampaignCreate, TranslationLanguage, PushNotificationGroup} from '@/types/push';
import { Languages, Users } from 'lucide-react';
import pushClient from '@/services/api/pushClient';

// Steps
const STEPS = [
    { id: 'site', label: 'Domínio', icon: Globe, desc: 'Escolha o domínio' },
    { id: 'content', label: 'Conteúdo', icon: FileText, desc: 'Título, texto e mídia' },
    { id: 'schedule', label: 'Agendamento', icon: Calendar, desc: 'Tipo e agendamento' },
    { id: 'audience', label: 'Segmentação', icon: MapPin, desc: 'Segmentação' },
    { id: 'tracking', label: 'UTM & Tags', icon: BarChart3, desc: 'UTM e tags' },
    { id: 'review', label: 'Revisar', icon: Check, desc: 'Revise e envie' },
];

// Frequency category type
type FrequencyCategory = 'interval' | 'daily' | 'weekly' | 'monthly';

interface GeoCountry {
    country: string;
    countryCode: string;
    totalLeads: number;
    states: {
        state: string;
        stateCode: string;
        totalLeads: number;
        cities: { city: string; totalLeads: number }[];
    }[];
}

// Cron → texto
function cronToText(cron: string): string {
    const parts = cron.split(' ');
    if (parts.length !== 5) return cron;
    const [min, hour, dom, , dow] = parts;
    const dayNames: Record<string, string> = { '0': 'Dom', '1': 'Seg', '2': 'Ter', '3': 'Qua', '4': 'Qui', '5': 'Sex', '6': 'Sáb' };
    const time = `${hour.padStart(2, '0')}:${min.padStart(2, '0')}`;
    if (min.startsWith('*/')) return `A cada ${min.replace('*/', '')} minutos`;
    if (hour.startsWith('*/')) {
        const h = Number(hour.replace('*/', ''));
        return `A cada ${h} ${h === 1 ? 'hora' : 'horas'}`;
    }
    if (dom !== '*' && dow === '*') return `Todo dia ${dom.split(',').join(' e ')} às ${time}`;
    if (dow !== '*') {
        const days = dow.includes('-')
            ? (() => { const [s, e] = dow.split('-').map(Number); return Array.from({ length: e - s + 1 }, (_, i) => dayNames[String(s + i)] || ''); })()
            : dow.split(',').map(d => dayNames[d.trim()] || d);
        return `${days.join(', ')} às ${time}`;
    }
    return `Todos os dias às ${time}`;
}

export default function CampaignCreate() {
    const [currentStep, setCurrentStep] = useState(0);
    const navigate = useNavigate();
    const location = useLocation();
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const duplicateData = (location.state as any)?.duplicate;

    const [targetType, setTargetType] = useState<'domain' | 'group'>('domain');

    // Form state
    const [form, setForm] = useState<{
        domain_id: number | null;
        group_id: number | null;
        name: string;
        title: string;
        body: string;
        url: string;
        icon: string;
        image: string;
        badge: string;
        utm_campaign: string;
        utm_medium: string;
        utm_source: string;
        tag_on_receive: string;
        tag_on_click: string;
        type: 'direct' | 'scheduled' | 'recurring';
        scheduled_at: string;
        recurring_cron: string;
        recurring_until: string;
        segment_countries: string[];
        segment_states: string[];
        segment_cities: string[];
        segment_tags: string[];
        segment_has_name: boolean;
        segment_has_email: boolean;
        translate_enabled: boolean;
        translate_mode: 'all' | 'specific';
        translate_languages: string[];
        source_language: string;
        translate_url_mode: 'none' | 'path' | 'subdomain';
    }>({
        domain_id: null,
        group_id: null,
        name: '',
        title: '',
        body: '',
        url: '',
        icon: '',
        image: '',
        badge: '',
        utm_campaign: '',
        utm_medium: 'push',
        utm_source: 'push_notification',
        tag_on_receive: '',
        tag_on_click: '',
        type: 'direct',
        scheduled_at: '',
        recurring_cron: '0 10 * * *',
        recurring_until: '',
        segment_countries: [],
        segment_states: [],
        segment_cities: [],
        segment_tags: [],
        segment_has_name: false,
        segment_has_email: false,
        translate_enabled: false,
        translate_mode: 'all',
        translate_languages: [],
        source_language: 'en',
        translate_url_mode: 'none',
    });

    // Pre-fill from duplicate
    useEffect(() => {
        if (duplicateData) {
            setForm(prev => ({
                ...prev,
                domain_id: duplicateData.domain_id ?? null,
                name: duplicateData.name ? `${duplicateData.name} (cópia)` : '',
                title: duplicateData.title || '',
                body: duplicateData.body || '',
                url: duplicateData.url || '',
                icon: duplicateData.icon || '',
                image: duplicateData.image || '',
                badge: duplicateData.badge || '',
                utm_campaign: duplicateData.utm_campaign || '',
                utm_medium: duplicateData.utm_medium || 'push',
                utm_source: duplicateData.utm_source || 'push_notification',
                tag_on_receive: duplicateData.tag_on_receive || '',
                tag_on_click: duplicateData.tag_on_click || '',
                type: duplicateData.type || 'direct',
                recurring_cron: duplicateData.recurring_cron || '0 10 * * *',
                recurring_until: duplicateData.recurring_until || '',
                segment_countries: duplicateData.segment_countries || [],
                segment_states: duplicateData.segment_states || [],
                segment_cities: duplicateData.segment_cities || [],
                segment_tags: duplicateData.segment_tags || [],
                segment_has_name: duplicateData.segment_has_name || false,
                segment_has_email: duplicateData.segment_has_email || false,
                translate_enabled: duplicateData.translate_enabled || false,
                translate_mode: duplicateData.translate_mode || 'all',
                translate_languages: duplicateData.translate_languages || [],
                source_language: duplicateData.source_language || 'en',
                translate_url_mode: duplicateData.translate_url_mode || 'none',
            }));
        }
    }, []);

    const [showMediaPicker, setShowMediaPicker] = useState<'icon' | 'image' | 'badge' | null>(null);
    const [uploading, setUploading] = useState(false);
    const [previewMode, setPreviewMode] = useState<'mobile' | 'desktop'>('mobile');
    const [frequencyCategory, setFrequencyCategory] = useState<FrequencyCategory>('daily');
    const [recurringTime, setRecurringTime] = useState('09:00');
    const [recurringDays, setRecurringDays] = useState<number[]>([]);
    const [recurringMonthDay, setRecurringMonthDay] = useState(1);
    const [intervalValue, setIntervalValue] = useState(1);
    const [intervalUnit, setIntervalUnit] = useState<'seconds' | 'minutes' | 'hours'>('hours');
    const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
    const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
    const [scheduledDay, setScheduledDay] = useState<number | null>(null);
    const [scheduledTime, setScheduledTime] = useState('14:00');

    // Queries
    const { data: domains = [] } = useQuery({ queryKey: ['push-domains'], queryFn: getPushDomains });
    const configuredDomains = domains.filter((d) => d.push_config?.mongo_domain_id);
    const { data: groups = [] } = useQuery<PushNotificationGroup[]>({ queryKey: ['push-groups'], queryFn: getPushGroups });

    const { data: domainTags = [] } = useQuery<PushTag[]>({
        queryKey: ['push-tags', form.domain_id],
        queryFn: () => getPushTags(form.domain_id!),
        enabled: !!form.domain_id,
    });

    const { data: mediaList = [], refetch: refetchMedia } = useQuery<MediaItem[]>({
        queryKey: ['media-list'],
        queryFn: () => getMedia(),
    });

    const { data: geoData } = useQuery<{ countries: GeoCountry[] }>({
        queryKey: ['push-geo', form.domain_id],
        queryFn: async () => {
            const resp = await pushClient.get(`/geo/${form.domain_id}`);
            return resp.data;
        },
        enabled: !!form.domain_id,
    });

    const { data: translationLangs = [] } = useQuery<TranslationLanguage[]>({
        queryKey: ['translation-languages'],
        queryFn: async () => {
            const res = await getTranslationLanguages();
            return res.languages;
        },
    });

    const geoCountries = geoData?.countries || [];

    // Selected country → states
    const selectedCountryData = geoCountries.filter(c => form.segment_countries.includes(c.countryCode));
    const availableStates = selectedCountryData.flatMap(c => c.states);
    const selectedStateData = availableStates.filter(s => form.segment_states.includes(s.stateCode));
    const availableCities = selectedStateData.flatMap(s => s.cities);

    // Create mutation
    const createMutation = useMutation({
        mutationFn: createPushCampaign,
        onSuccess: () => {
            toast({ title: 'Campanha criada com sucesso!' });
            queryClient.invalidateQueries({ queryKey: ['push-campaigns'] });
            navigate('/push/campaigns');
        },
        onError: (e: any) => toast({ variant: 'destructive', title: 'Erro', description: e?.response?.data?.detail || e.message }),
    });

    const handleSubmit = () => {
        if ((!form.domain_id && !form.group_id) || !form.name || !form.title || !form.body || !form.url) {
            toast({ variant: 'destructive', title: 'Preencha todos os campos obrigatórios' });
            return;
        }

        const payload: PushCampaignCreate = {
            ...(form.domain_id && { domain_id: form.domain_id }),
            ...(form.group_id && { group_id: form.group_id }),
            name: form.name,
            type: form.type,
            title: form.title,
            body: form.body,
            url: form.url,
            ...(form.icon && { icon: form.icon }),
            ...(form.image && { image: form.image }),
            ...(form.badge && { badge: form.badge }),
            ...(form.utm_campaign && { utm_campaign: form.utm_campaign }),
            ...(form.utm_medium && { utm_medium: form.utm_medium }),
            ...(form.utm_source && { utm_source: form.utm_source }),
            ...(form.tag_on_receive && { tag_on_receive: form.tag_on_receive }),
            ...(form.tag_on_click && { tag_on_click: form.tag_on_click }),
            ...(form.type === 'scheduled' && form.scheduled_at && { scheduled_at: form.scheduled_at }),
            ...(form.type === 'recurring' && form.recurring_cron && { recurring_cron: form.recurring_cron }),
            ...(form.type === 'recurring' && form.recurring_until && { recurring_until: form.recurring_until }),
            ...(form.segment_countries.length > 0 && { segment_countries: form.segment_countries }),
            ...(form.segment_states.length > 0 && { segment_states: form.segment_states }),
            ...(form.segment_cities.length > 0 && { segment_cities: form.segment_cities }),
            ...(form.segment_tags.length > 0 && { segment_tags: form.segment_tags }),
            ...(form.segment_has_name && { segment_has_name: true }),
            ...(form.segment_has_email && { segment_has_email: true }),
            ...(form.translate_enabled && {
                translate_enabled: true,
                translate_mode: form.translate_mode,
                source_language: form.source_language,
                translate_url_mode: form.translate_url_mode,
                ...(form.translate_mode === 'specific' && form.translate_languages.length > 0 && {
                    translate_languages: form.translate_languages,
                }),
            }),
        };

        createMutation.mutate(payload);
    };

    const canAdvance = () => {
        switch (currentStep) {
            case 0: return !!form.domain_id || !!form.group_id;
            case 1: return !!form.name && !!form.title && !!form.body && !!form.url;
            case 2: {
                if (form.type === 'scheduled') return !!form.scheduled_at;
                if (form.type === 'recurring') return !!form.recurring_cron;
                return true;
            }
            case 3: return true;
            case 4: return true;
            case 5: return true;
            default: return true;
        }
    };

    // Build cron from frequency category selections
    const buildCronFromCategory = (category: FrequencyCategory, time: string, days: number[], monthDay: number, intVal: number, intUnit: 'seconds' | 'minutes' | 'hours') => {
        const [h, m] = time.split(':').map(Number);
        switch (category) {
            case 'interval':
                // 6-field cron (seconds minutes hours day month dow) when seconds; otherwise standard 5-field
                if (intUnit === 'seconds') return `*/${intVal} * * * * *`;
                if (intUnit === 'minutes') return `*/${intVal} * * * *`;
                return `0 */${intVal} * * *`;
            case 'daily':
                return `${m} ${h} * * *`;
            case 'weekly': {
                if (days.length === 0) return `${m} ${h} * * *`;
                return `${m} ${h} * * ${days.sort().join(',')}`;
            }
            case 'monthly':
                return `${m} ${h} ${monthDay} * *`;
            default:
                return `${m} ${h} * * *`;
        }
    };

    // Sync cron when category settings change
    useEffect(() => {
        if (form.type === 'recurring') {
            const newCron = buildCronFromCategory(frequencyCategory, recurringTime, recurringDays, recurringMonthDay, intervalValue, intervalUnit);
            updateForm({ recurring_cron: newCron });
        }
    }, [frequencyCategory, recurringTime, recurringDays, recurringMonthDay, intervalValue, intervalUnit]);

    const updateForm = (patch: Partial<typeof form>) => setForm(prev => ({ ...prev, ...patch }));

    // Domínio selecionado
    const selectedDomain = configuredDomains.find(d => d.id === form.domain_id);

    const [showCreateTag, setShowCreateTag] = useState(false);
    const [newTag, setNewTag] = useState({ name: '', color: '#6B7280', description: '' });
    const [creatingTag, setCreatingTag] = useState(false);

    // Default URL com domínio quando seleciona o site
    useEffect(() => {
        if (selectedDomain && !form.url) {
            updateForm({ url: `https://${selectedDomain.domain}/` });
        }
    }, [selectedDomain?.domain]);

    // Sync calendar → scheduled_at
    useEffect(() => {
        if (scheduledDay !== null) {
            const [h, m] = scheduledTime.split(':').map(Number);
            const date = new Date(calendarYear, calendarMonth, scheduledDay, h, m);
            const pad = (n: number) => String(n).padStart(2, '0');
            const iso = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(h)}:${pad(m)}`;
            updateForm({ scheduled_at: iso });
        }
    }, [scheduledDay, scheduledTime, calendarMonth, calendarYear]);

    // Calendar helpers
    const calendarMonthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
    const calendarDayNames = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
    const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
    const getFirstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay();
    const prevMonth = () => {
        if (calendarMonth === 0) { setCalendarMonth(11); setCalendarYear(y => y - 1); }
        else setCalendarMonth(m => m - 1);
    };
    const nextMonth = () => {
        if (calendarMonth === 11) { setCalendarMonth(0); setCalendarYear(y => y + 1); }
        else setCalendarMonth(m => m + 1);
    };
    const today = new Date();
    const isToday = (day: number) => day === today.getDate() && calendarMonth === today.getMonth() && calendarYear === today.getFullYear();
    const isPast = (day: number) => {
        const d = new Date(calendarYear, calendarMonth, day);
        const t = new Date(); t.setHours(0, 0, 0, 0);
        return d < t;
    };

    // Upload handler
    const handleFileUpload = async (file: File) => {
        if (!showMediaPicker) return;
        setUploading(true);
        try {
            const isIcon = showMediaPicker === 'icon' || showMediaPicker === 'badge';
            const media = await uploadMedia(file, isIcon);
            updateForm({ [showMediaPicker]: getMediaUrl(media.filepath) });
            refetchMedia();
            setShowMediaPicker(null);
            toast({ title: 'Upload concluído!' });
        } catch (e: any) {
            toast({ variant: 'destructive', title: 'Erro no upload', description: e.message });
        } finally {
            setUploading(false);
        }
    };

    // Parse UTM da URL
    const parseUtmFromUrl = (url: string) => {
        try {
            const parsed = new URL(url);
            const utmSource = parsed.searchParams.get('utm_source');
            const utmMedium = parsed.searchParams.get('utm_medium');
            const utmCampaign = parsed.searchParams.get('utm_campaign');

            if (utmSource || utmMedium || utmCampaign) {
                // Remove UTMs da URL
                parsed.searchParams.delete('utm_source');
                parsed.searchParams.delete('utm_medium');
                parsed.searchParams.delete('utm_campaign');
                parsed.searchParams.delete('utm_term');
                parsed.searchParams.delete('utm_content');

                const cleanUrl = parsed.toString();
                updateForm({
                    url: cleanUrl,
                    ...(utmSource && { utm_source: utmSource }),
                    ...(utmMedium && { utm_medium: utmMedium }),
                    ...(utmCampaign && { utm_campaign: utmCampaign }),
                });
                toast({ title: 'UTMs extraídos da URL', description: 'Parâmetros UTM foram movidos para a seção de rastreamento.' });
            }
        } catch {
            // URL inválida, ignora
        }
    };

    return (
        <div className="min-h-screen">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
                <button onClick={() => navigate('/push/campaigns')} className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white hover:bg-gray-50 transition-colors">
                    <ArrowLeft className="h-4 w-4 text-gray-600" />
                </button>
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100">
                    <Send className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Nova Campanha</h1>
                    <p className="text-sm text-gray-500">Crie e configure sua campanha de push notification</p>
                </div>
            </div>

            {/* Steps indicator */}
            <div className="mb-8 flex items-center gap-0">
                {STEPS.map((step, i) => {
                    const Icon = step.icon;
                    const isActive = i === currentStep;
                    const isDone = i < currentStep;
                    return (
                        <div key={step.id} className="flex items-center flex-1">
                            <button
                                onClick={() => i < currentStep && setCurrentStep(i)}
                                className={`flex items-center gap-2 rounded-full px-3.5 py-2 text-xs font-medium transition-all ${
                                    isActive ? 'bg-emerald-600 text-white shadow-sm' : isDone ? 'text-emerald-600 cursor-pointer hover:bg-emerald-50' : 'text-gray-400'
                                }`}
                            >
                                {isDone ? (
                                    <div className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500">
                                        <Check className="h-2.5 w-2.5 text-white" />
                                    </div>
                                ) : (
                                    <Icon className="h-3.5 w-3.5" />
                                )}
                                <span className="hidden sm:inline">{step.label}</span>
                            </button>
                            {i < STEPS.length - 1 && <div className="mx-0.5 h-px flex-1 max-w-6 bg-gray-200" />}
                        </div>
                    );
                })}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main content */}
                <div className="lg:col-span-2 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">

                    {/* STEP 0: Site */}
                    {currentStep === 0 && (
                        <div className="space-y-6">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900">Escolha o destino</h2>
                                <p className="mt-1 text-sm text-gray-500">Selecione um dominio individual ou um grupo de notificacao</p>
                            </div>

                            {/* Target type toggle */}
                            <div className="flex items-center gap-1 rounded-xl bg-gray-100 p-1 w-fit">
                                <button
                                    onClick={() => { setTargetType('domain'); updateForm({ group_id: null }); }}
                                    className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all ${
                                        targetType === 'domain' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    <Globe className="h-4 w-4" /> Dominio
                                </button>
                                <button
                                    onClick={() => { setTargetType('group'); updateForm({ domain_id: null }); }}
                                    className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all ${
                                        targetType === 'group' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    <Users className="h-4 w-4" /> Grupo
                                </button>
                            </div>

                            <div className="grid gap-3">
                                {targetType === 'domain' && (
                                    <>
                                        {configuredDomains.map((d) => (
                                            <button
                                                key={d.id}
                                                onClick={() => updateForm({ domain_id: d.id, group_id: null })}
                                                className={`flex items-center gap-4 rounded-xl border-2 p-4 text-left transition-all ${
                                                    form.domain_id === d.id ? 'border-gray-900 bg-gray-50' : 'border-gray-100 hover:border-gray-300'
                                                }`}
                                            >
                                                <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${form.domain_id === d.id ? 'bg-gray-900' : 'bg-gray-100'}`}>
                                                    <Globe className={`h-5 w-5 ${form.domain_id === d.id ? 'text-white' : 'text-gray-500'}`} />
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-sm font-semibold text-gray-900">{d.domain}</p>
                                                    <p className="text-xs text-gray-400">Push configurado</p>
                                                </div>
                                                {form.domain_id === d.id && <Check className="h-5 w-5 text-gray-900" />}
                                            </button>
                                        ))}
                                        {configuredDomains.length === 0 && (
                                            <p className="text-sm text-gray-400 text-center py-8">Nenhum dominio configurado. Configure na secao de Setup.</p>
                                        )}
                                    </>
                                )}
                                {targetType === 'group' && (
                                    <>
                                        {groups.map((g) => (
                                            <button
                                                key={g.id}
                                                onClick={() => updateForm({ group_id: g.id, domain_id: null })}
                                                className={`flex items-center gap-4 rounded-xl border-2 p-4 text-left transition-all ${
                                                    form.group_id === g.id ? 'border-violet-600 bg-violet-50' : 'border-gray-100 hover:border-violet-300'
                                                }`}
                                            >
                                                <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${form.group_id === g.id ? 'bg-violet-600' : 'bg-gray-100'}`}>
                                                    <Users className={`h-5 w-5 ${form.group_id === g.id ? 'text-white' : 'text-gray-500'}`} />
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-sm font-semibold text-gray-900">{g.name}</p>
                                                    <p className="text-xs text-gray-400">{g.domain_count} dominio{g.domain_count !== 1 ? 's' : ''}</p>
                                                </div>
                                                {form.group_id === g.id && <Check className="h-5 w-5 text-violet-600" />}
                                            </button>
                                        ))}
                                        {groups.length === 0 && (
                                            <p className="text-sm text-gray-400 text-center py-8">Nenhum grupo criado. Crie grupos na secao de Grupos.</p>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {/* STEP 1: Conteúdo */}
                    {currentStep === 1 && (
                        <div className="space-y-5">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900">Conteúdo da notificação</h2>
                                <p className="mt-1 text-sm text-gray-500">Defina o que seus leads vão ver</p>
                            </div>

                            <div>
                                <Label className="text-xs text-gray-500">Nome interno da campanha *</Label>
                                <Input value={form.name} onChange={(e) => updateForm({ name: e.target.value })} placeholder="ex: Promo Black Friday" className="mt-1 rounded-lg" />
                            </div>

                            <div>
                                <Label className="text-xs text-gray-500">Título da notificação *</Label>
                                <Input value={form.title} onChange={(e) => updateForm({ title: e.target.value })} placeholder="🔥 Oferta imperdível!" className="mt-1 rounded-lg" maxLength={100} />
                                <p className="mt-1 text-xs text-gray-300">{form.title.length}/100</p>
                            </div>

                            <div>
                                <Label className="text-xs text-gray-500">Mensagem *</Label>
                                <Textarea value={form.body} onChange={(e) => updateForm({ body: e.target.value })} placeholder="Aproveite 50% de desconto em todos os produtos..." className="mt-1 rounded-lg resize-none" rows={3} maxLength={500} />
                                <p className="mt-1 text-xs text-gray-300">{form.body.length}/500</p>
                            </div>

                            <div>
                                <Label className="text-xs text-gray-500">URL de destino *</Label>
                                <Input
                                    value={form.url}
                                    onChange={(e) => updateForm({ url: e.target.value })}
                                    onBlur={(e) => parseUtmFromUrl(e.target.value)}
                                    placeholder={selectedDomain ? `https://${selectedDomain.domain}/promo` : 'https://seusite.com/promo'}
                                    className="mt-1 rounded-lg"
                                />
                                <p className="mt-1 text-[11px] text-gray-400">Se a URL contiver parâmetros UTM, eles serão extraídos automaticamente para a aba de rastreamento.</p>
                            </div>

                            {/* Icon */}
                            <div>
                                <Label className="text-xs text-gray-500">Ícone</Label>
                                <div className="mt-1.5 flex items-center gap-3">
                                    {form.icon ? (
                                        <div className="relative shrink-0">
                                            <img src={form.icon} alt="icon" className="h-12 w-12 rounded-lg object-cover border" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                            <button onClick={() => updateForm({ icon: '' })} className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-red-500 text-white flex items-center justify-center"><X className="h-3 w-3" /></button>
                                        </div>
                                    ) : null}
                                    <button onClick={() => setShowMediaPicker('icon')} className="flex items-center gap-2 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-xs text-gray-500 hover:border-gray-400 hover:text-gray-600 transition-colors shrink-0">
                                        <ImageIcon className="h-3.5 w-3.5" /> Biblioteca
                                    </button>
                                    <Input value={form.icon} onChange={(e) => updateForm({ icon: e.target.value })} placeholder="ou cole a URL do ícone" className="flex-1 rounded-lg text-sm h-9" />
                                </div>
                            </div>

                            {/* Image */}
                            <div>
                                <Label className="text-xs text-gray-500">Imagem</Label>
                                <div className="mt-1.5 space-y-2">
                                    {form.image && (
                                        <div className="relative inline-block">
                                            <img src={form.image} alt="banner" className="h-24 rounded-lg object-cover border" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                            <button onClick={() => updateForm({ image: '' })} className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-red-500 text-white flex items-center justify-center"><X className="h-3 w-3" /></button>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-3">
                                        <button onClick={() => setShowMediaPicker('image')} className="flex items-center gap-2 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-xs text-gray-500 hover:border-gray-400 hover:text-gray-600 transition-colors shrink-0">
                                            <ImageIcon className="h-3.5 w-3.5" /> Biblioteca
                                        </button>
                                        <Input value={form.image} onChange={(e) => updateForm({ image: e.target.value })} placeholder="ou cole a URL da imagem" className="flex-1 rounded-lg text-sm h-9" />
                                    </div>
                                </div>
                            </div>

                            {/* Badge */}
                            <div>
                                <Label className="text-xs text-gray-500">Badge <span className="text-gray-300">(opcional)</span></Label>
                                <div className="mt-1.5 flex items-center gap-3">
                                    {form.badge ? (
                                        <div className="relative shrink-0">
                                            <img src={form.badge} alt="badge" className="h-8 w-8 rounded object-cover border" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                            <button onClick={() => updateForm({ badge: '' })} className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-red-500 text-white flex items-center justify-center"><X className="h-2.5 w-2.5" /></button>
                                        </div>
                                    ) : null}
                                    <button onClick={() => setShowMediaPicker('badge')} className="flex items-center gap-2 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-xs text-gray-500 hover:border-gray-400 hover:text-gray-600 transition-colors shrink-0">
                                        <ImageIcon className="h-3.5 w-3.5" /> Biblioteca
                                    </button>
                                    <Input value={form.badge} onChange={(e) => updateForm({ badge: e.target.value })} placeholder="ou cole a URL do badge" className="flex-1 rounded-lg text-sm h-9" />
                                </div>
                            </div>

                            {/* Tradução Automática */}
                            <div className="border-t pt-5 mt-2">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Languages className="h-4 w-4 text-gray-400" />
                                        <div>
                                            <Label className="text-sm font-medium text-gray-700">Tradução automática</Label>
                                            <p className="text-xs text-gray-400">Traduz a notificação com base no país do lead</p>
                                        </div>
                                    </div>
                                    <Switch
                                        checked={form.translate_enabled}
                                        onCheckedChange={(checked) => updateForm({
                                            translate_enabled: checked,
                                            translate_mode: checked ? 'all' : 'all' as const,
                                        })}
                                    />
                                </div>

                                {form.translate_enabled && (
                                    <div className="mt-4 space-y-4 pl-6 border-l-2 border-gray-100">
                                        <div>
                                            <Label className="text-xs text-gray-500">Idioma do conteúdo original</Label>
                                            <Select value={form.source_language} onValueChange={(v) => updateForm({ source_language: v })}>
                                                <SelectTrigger className="mt-1 rounded-lg w-56">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {translationLangs.map((lang) => (
                                                        <SelectItem key={lang.code} value={lang.code}>{lang.name}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        <div className="space-y-2">
                                            <Label className="text-xs text-gray-500">Modo de tradução</Label>
                                            <div className="flex gap-3">
                                                <button
                                                    onClick={() => updateForm({ translate_mode: 'all' as const })}
                                                    className={`flex-1 rounded-lg border-2 p-3 text-left transition-all ${
                                                        form.translate_mode === 'all' ? 'border-emerald-500 bg-emerald-50/30' : 'border-gray-100 hover:border-gray-300'
                                                    }`}
                                                >
                                                    <p className="text-sm font-medium text-gray-700">Todos os idiomas</p>
                                                    <p className="text-xs text-gray-400 mt-0.5">Detecta automaticamente pelo país do lead</p>
                                                </button>
                                                <button
                                                    onClick={() => updateForm({ translate_mode: 'specific' as const })}
                                                    className={`flex-1 rounded-lg border-2 p-3 text-left transition-all ${
                                                        form.translate_mode === 'specific' ? 'border-emerald-500 bg-emerald-50/30' : 'border-gray-100 hover:border-gray-300'
                                                    }`}
                                                >
                                                    <p className="text-sm font-medium text-gray-700">Idiomas específicos</p>
                                                    <p className="text-xs text-gray-400 mt-0.5">Escolha para quais idiomas traduzir</p>
                                                </button>
                                            </div>
                                        </div>

                                        {form.translate_mode === 'specific' && (
                                            <div>
                                                <Label className="text-xs text-gray-500">Idiomas alvo</Label>
                                                <div className="mt-2 flex flex-wrap gap-2">
                                                    {translationLangs
                                                        .filter((lang) => lang.code !== form.source_language)
                                                        .map((lang) => {
                                                            const isSelected = form.translate_languages.includes(lang.code);
                                                            return (
                                                                <button
                                                                    key={lang.code}
                                                                    onClick={() => {
                                                                        const langs = isSelected
                                                                            ? form.translate_languages.filter((l) => l !== lang.code)
                                                                            : [...form.translate_languages, lang.code];
                                                                        updateForm({ translate_languages: langs });
                                                                    }}
                                                                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-all ${
                                                                        isSelected
                                                                            ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                                                                            : 'bg-gray-50 text-gray-600 border border-gray-200 hover:border-gray-300'
                                                                    }`}
                                                                >
                                                                    {isSelected && <Check className="h-3 w-3" />}
                                                                    {lang.name}
                                                                </button>
                                                            );
                                                        })}
                                                </div>
                                            </div>
                                        )}

                                        <div className="space-y-2">
                                            <Label className="text-xs text-gray-500">Modo de URL traduzida</Label>
                                            <p className="text-xs text-gray-400">Adapta a URL da notificação para o idioma do lead</p>
                                            <div className="flex gap-3">
                                                {([
                                                    { value: 'none' as const, label: 'Não alterar', desc: 'Mesma URL para todos' },
                                                    { value: 'path' as const, label: 'Path', desc: 'site.com/es/pagina' },
                                                    { value: 'subdomain' as const, label: 'Subdomínio', desc: 'es.site.com/pagina' },
                                                ] as const).map((opt) => (
                                                    <button
                                                        key={opt.value}
                                                        onClick={() => updateForm({ translate_url_mode: opt.value })}
                                                        className={`flex-1 rounded-lg border-2 p-3 text-left transition-all ${
                                                            form.translate_url_mode === opt.value ? 'border-emerald-500 bg-emerald-50/30' : 'border-gray-100 hover:border-gray-300'
                                                        }`}
                                                    >
                                                        <p className="text-sm font-medium text-gray-700">{opt.label}</p>
                                                        <p className="text-xs text-gray-400 mt-0.5">{opt.desc}</p>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Media picker modal com upload */}
                            {showMediaPicker && (
                                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowMediaPicker(null)}>
                                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[75vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                                        <div className="flex items-center justify-between p-4 border-b">
                                            <h3 className="text-sm font-semibold text-gray-900">
                                                {{ icon: 'Escolher Ícone', image: 'Escolher Imagem', badge: 'Escolher Badge' }[showMediaPicker]}
                                            </h3>
                                            <button onClick={() => setShowMediaPicker(null)} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
                                        </div>

                                        {/* Upload area */}
                                        <div className="px-4 pt-4">
                                            <label className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-5 cursor-pointer transition-all ${uploading ? 'border-blue-300 bg-blue-50' : 'border-gray-200 hover:border-gray-400 hover:bg-gray-50'}`}>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    className="hidden"
                                                    disabled={uploading}
                                                    onChange={(e) => {
                                                        const file = e.target.files?.[0];
                                                        if (file) handleFileUpload(file);
                                                        e.target.value = '';
                                                    }}
                                                />
                                                {uploading ? (
                                                    <>
                                                        <Loader2 className="h-6 w-6 text-blue-500 animate-spin" />
                                                        <p className="mt-2 text-xs text-blue-600 font-medium">Enviando...</p>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Upload className="h-6 w-6 text-gray-400" />
                                                        <p className="mt-2 text-xs text-gray-600 font-medium">Fazer upload</p>
                                                        <p className="text-[11px] text-gray-400">JPG, PNG, GIF, WebP, SVG</p>
                                                    </>
                                                )}
                                            </label>
                                        </div>

                                        {/* Biblioteca */}
                                        <div className="px-4 pt-3 pb-1">
                                            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Biblioteca</p>
                                        </div>
                                        <div className="flex-1 overflow-y-auto px-4 pb-4">
                                            <div className="grid grid-cols-4 gap-2.5">
                                                {(showMediaPicker === 'icon' || showMediaPicker === 'badge'
                                                        ? mediaList.filter(m => m.media_type === 'icon')
                                                        : mediaList.filter(m => m.media_type === 'image')
                                                ).map((media) => (
                                                    <button
                                                        key={media.id}
                                                        onClick={() => {
                                                            updateForm({ [showMediaPicker]: getMediaUrl(media.filepath) });
                                                            setShowMediaPicker(null);
                                                        }}
                                                        className="group relative rounded-lg border-2 border-transparent hover:border-gray-900 overflow-hidden transition-all"
                                                    >
                                                        <img src={getThumbnailUrl(media)} alt={media.original_filename} className="w-full aspect-square object-cover" />
                                                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                                                            <Check className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                                        </div>
                                                    </button>
                                                ))}
                                                {mediaList.filter(m => (showMediaPicker === 'icon' || showMediaPicker === 'badge') ? m.media_type === 'icon' : m.media_type === 'image').length === 0 && (
                                                    <div className="col-span-4 py-6 text-center text-sm text-gray-400">
                                                        Nenhuma mídia encontrada. Faça upload acima.
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* STEP 2: Schedule */}
                    {currentStep === 2 && (
                        <div className="space-y-5">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900">Tipo de Campanha</h2>
                            </div>

                            <div className="grid grid-cols-3 gap-3">
                                {([
                                    { type: 'direct' as const, label: 'Envio Direto', desc: 'Disparar imediatamente após criar', icon: Zap },
                                    { type: 'scheduled' as const, label: 'Agendada', desc: 'Definir data e hora para o envio', icon: CalendarDays },
                                    { type: 'recurring' as const, label: 'Recorrente', desc: 'Repetir automaticamente em intervalos', icon: Repeat },
                                ]).map((opt) => {
                                    const Icon = opt.icon;
                                    const isSelected = form.type === opt.type;
                                    return (
                                        <button
                                            key={opt.type}
                                            onClick={() => updateForm({ type: opt.type })}
                                            className={`relative flex flex-col items-start gap-2 rounded-xl border-2 p-4 transition-all text-left ${
                                                isSelected ? 'border-emerald-500 bg-emerald-50/30' : 'border-gray-100 hover:border-gray-300'
                                            }`}
                                        >
                                            {isSelected && (
                                                <div className="absolute top-2.5 right-2.5 h-3 w-3 rounded-full bg-emerald-500" />
                                            )}
                                            <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${isSelected ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-400'}`}>
                                                <Icon className="h-4.5 w-4.5" />
                                            </div>
                                            <p className={`text-sm font-semibold ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>{opt.label}</p>
                                            <p className="text-xs text-gray-400 leading-relaxed">{opt.desc}</p>
                                        </button>
                                    );
                                })}
                            </div>

                            {form.type === 'scheduled' && (
                                <div>
                                    <Label className="text-xs font-medium text-gray-700">Data e Hora do Envio</Label>
                                    <div className="mt-2 rounded-xl border border-gray-200 p-4 max-w-md">
                                        {/* Month navigation */}
                                        <div className="flex items-center justify-between mb-3">
                                            <button onClick={prevMonth} className="flex h-7 w-7 items-center justify-center rounded-lg hover:bg-gray-100 transition-colors">
                                                <ChevronLeft className="h-4 w-4 text-gray-500" />
                                            </button>
                                            <span className="text-sm font-semibold text-gray-900">
                                                {calendarMonthNames[calendarMonth]} De {calendarYear}
                                            </span>
                                            <button onClick={nextMonth} className="flex h-7 w-7 items-center justify-center rounded-lg hover:bg-gray-100 transition-colors">
                                                <ChevronRight className="h-4 w-4 text-gray-500" />
                                            </button>
                                        </div>

                                        {/* Day names header */}
                                        <div className="grid grid-cols-7 mb-1">
                                            {calendarDayNames.map((d, i) => (
                                                <div key={i} className="text-center text-[11px] font-medium text-gray-400 py-1">{d}</div>
                                            ))}
                                        </div>

                                        {/* Day grid */}
                                        <div className="grid grid-cols-7 gap-0.5">
                                            {/* Empty cells for offset */}
                                            {Array.from({ length: getFirstDayOfMonth(calendarYear, calendarMonth) }).map((_, i) => (
                                                <div key={`empty-${i}`} />
                                            ))}
                                            {/* Day cells */}
                                            {Array.from({ length: getDaysInMonth(calendarYear, calendarMonth) }, (_, i) => i + 1).map((day) => {
                                                const selected = scheduledDay === day && calendarMonth === (form.scheduled_at ? new Date(form.scheduled_at).getMonth() : -1) && calendarYear === (form.scheduled_at ? new Date(form.scheduled_at).getFullYear() : -1);
                                                const past = isPast(day);
                                                return (
                                                    <button
                                                        key={day}
                                                        disabled={past}
                                                        onClick={() => setScheduledDay(day)}
                                                        className={`h-8 w-8 mx-auto rounded-full text-xs font-medium transition-all flex items-center justify-center ${
                                                            selected
                                                                ? 'bg-indigo-600 text-white'
                                                                : isToday(day)
                                                                    ? 'bg-indigo-100 text-indigo-700 font-bold'
                                                                    : past
                                                                        ? 'text-gray-300 cursor-not-allowed'
                                                                        : 'text-gray-700 hover:bg-gray-100'
                                                        }`}
                                                    >
                                                        {day}
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        {/* Time + summary */}
                                        <div className="mt-4 flex items-center gap-3 border-t border-gray-100 pt-3">
                                            <span className="text-xs text-gray-500">Horário:</span>
                                            <Input
                                                type="time"
                                                value={scheduledTime}
                                                onChange={(e) => setScheduledTime(e.target.value)}
                                                className="rounded-lg w-28 text-sm"
                                            />
                                            {scheduledDay && (
                                                <span className="text-xs text-gray-500 ml-auto">
                                                    {scheduledDay} de {calendarMonthNames[calendarMonth].toLowerCase().slice(0, 3)}. de {calendarYear} · {scheduledTime}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {form.type === 'recurring' && (
                                <>
                                    <div>
                                        <Label className="text-xs font-medium text-gray-700">Frequência</Label>
                                        <div className="mt-2 grid grid-cols-4 gap-2">
                                            {([
                                                { cat: 'interval' as FrequencyCategory, label: 'Intervalo', desc: 'A cada X min/horas' },
                                                { cat: 'daily' as FrequencyCategory, label: 'Diário', desc: 'Todo dia' },
                                                { cat: 'weekly' as FrequencyCategory, label: 'Semanal', desc: 'Dias da semana' },
                                                { cat: 'monthly' as FrequencyCategory, label: 'Mensal', desc: 'Dia do mês' },
                                            ]).map((opt) => {
                                                const isSelected = frequencyCategory === opt.cat;
                                                return (
                                                    <button
                                                        key={opt.cat}
                                                        onClick={() => setFrequencyCategory(opt.cat)}
                                                        className={`flex flex-col items-start rounded-xl border-2 p-3 transition-all text-left ${
                                                            isSelected ? 'border-emerald-500 bg-emerald-50/30' : 'border-gray-100 hover:border-gray-300'
                                                        }`}
                                                    >
                                                        <p className={`text-sm font-semibold ${isSelected ? 'text-gray-900' : 'text-gray-600'}`}>{opt.label}</p>
                                                        <p className="text-[11px] text-gray-400 mt-0.5">{opt.desc}</p>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {frequencyCategory === 'interval' && (
                                        <div className="space-y-4">
                                            <div>
                                                <Label className="text-xs text-gray-500">Repetir a cada</Label>
                                                <div className="mt-2 flex items-center gap-2">
                                                    <Input
                                                        type="number"
                                                        min={intervalUnit === 'seconds' ? 30 : 1}
                                                        max={intervalUnit === 'seconds' ? 59 : intervalUnit === 'minutes' ? 59 : 23}
                                                        value={intervalValue}
                                                        onChange={(e) => setIntervalValue(Math.max(intervalUnit === 'seconds' ? 30 : 1, Number(e.target.value)))}
                                                        className="rounded-lg w-20 text-center font-medium"
                                                    />
                                                    <div className="flex items-center gap-0 rounded-lg bg-gray-100 p-0.5">
                                                        <button
                                                            onClick={() => { setIntervalUnit('seconds'); setIntervalValue((v) => Math.max(30, v)); }}
                                                            className={`rounded-md px-3 py-1.5 text-xs font-medium transition-all ${
                                                                intervalUnit === 'seconds' ? 'bg-emerald-500 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
                                                            }`}
                                                        >
                                                            Segundos
                                                        </button>
                                                        <button
                                                            onClick={() => setIntervalUnit('minutes')}
                                                            className={`rounded-md px-3 py-1.5 text-xs font-medium transition-all ${
                                                                intervalUnit === 'minutes' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                                                            }`}
                                                        >
                                                            Minutos
                                                        </button>
                                                        <button
                                                            onClick={() => setIntervalUnit('hours')}
                                                            className={`rounded-md px-3 py-1.5 text-xs font-medium transition-all ${
                                                                intervalUnit === 'hours' ? 'bg-emerald-500 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
                                                            }`}
                                                        >
                                                            Horas
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex flex-wrap gap-1.5">
                                                {[
                                                    { label: '30 seg', val: 30, unit: 'seconds' as const },
                                                    { label: '1 min', val: 1, unit: 'minutes' as const },
                                                    { label: '30 min', val: 30, unit: 'minutes' as const },
                                                    { label: '1h', val: 1, unit: 'hours' as const },
                                                    { label: '2h', val: 2, unit: 'hours' as const },
                                                    { label: '4h', val: 4, unit: 'hours' as const },
                                                    { label: '6h', val: 6, unit: 'hours' as const },
                                                    { label: '8h', val: 8, unit: 'hours' as const },
                                                    { label: '12h', val: 12, unit: 'hours' as const },
                                                ].map((preset) => {
                                                    const isSelected = intervalValue === preset.val && intervalUnit === preset.unit;
                                                    return (
                                                        <button
                                                            key={preset.label}
                                                            onClick={() => { setIntervalValue(preset.val); setIntervalUnit(preset.unit); }}
                                                            className={`rounded-full border px-3 py-1 text-xs font-medium transition-all ${
                                                                isSelected ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 text-gray-500 hover:border-gray-400'
                                                            }`}
                                                        >
                                                            {preset.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {frequencyCategory === 'daily' && (
                                        <div>
                                            <Label className="text-xs text-gray-500 flex items-center gap-1.5">
                                                <Clock className="h-3.5 w-3.5" /> Horário do envio
                                            </Label>
                                            <Input type="time" value={recurringTime} onChange={(e) => setRecurringTime(e.target.value)} className="mt-1.5 rounded-lg w-32" />
                                        </div>
                                    )}

                                    {frequencyCategory === 'weekly' && (
                                        <div className="space-y-3">
                                            <Label className="text-xs text-gray-500">Dias da semana</Label>
                                            <div className="flex gap-1.5">
                                                {[
                                                    { val: 0, label: 'D' },
                                                    { val: 1, label: 'S' },
                                                    { val: 2, label: 'T' },
                                                    { val: 3, label: 'Q' },
                                                    { val: 4, label: 'Q' },
                                                    { val: 5, label: 'S' },
                                                    { val: 6, label: 'S' },
                                                ].map((day) => {
                                                    const isSelected = recurringDays.includes(day.val);
                                                    return (
                                                        <button
                                                            key={day.val}
                                                            onClick={() => setRecurringDays(prev => isSelected ? prev.filter(d => d !== day.val) : [...prev, day.val])}
                                                            className={`h-9 w-9 rounded-lg text-xs font-semibold transition-all ${
                                                                isSelected ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                                            }`}
                                                        >
                                                            {day.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            <div>
                                                <Label className="text-xs text-gray-500 flex items-center gap-1.5">
                                                    <Clock className="h-3.5 w-3.5" /> Horário do envio
                                                </Label>
                                                <Input type="time" value={recurringTime} onChange={(e) => setRecurringTime(e.target.value)} className="mt-1.5 rounded-lg w-32" />
                                            </div>
                                        </div>
                                    )}

                                    {frequencyCategory === 'monthly' && (
                                        <div className="space-y-4">
                                            <div>
                                                <Label className="text-xs text-gray-500">Dia do mês</Label>
                                                <div className="mt-2 flex flex-wrap gap-1.5">
                                                    {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => {
                                                        const isSelected = recurringMonthDay === day;
                                                        return (
                                                            <button
                                                                key={day}
                                                                onClick={() => setRecurringMonthDay(day)}
                                                                className={`h-8 w-8 rounded-lg text-xs font-medium transition-all ${
                                                                    isSelected ? 'bg-emerald-500 text-white' : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                                                                }`}
                                                            >
                                                                {day}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                                <p className="mt-2 text-[11px] text-gray-400">Limitado a 28 para funcionar em todos os meses</p>
                                            </div>
                                            <div>
                                                <Label className="text-xs text-gray-500 flex items-center gap-1.5">
                                                    <Clock className="h-3.5 w-3.5" /> Horário do envio
                                                </Label>
                                                <Input type="time" value={recurringTime} onChange={(e) => setRecurringTime(e.target.value)} className="mt-1.5 rounded-lg w-32" />
                                            </div>
                                        </div>
                                    )}

                                    {/* Cron summary badge */}
                                    <div className="flex items-center gap-2.5 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3">
                                        <Repeat className="h-4 w-4 text-emerald-600 shrink-0" />
                                        <div>
                                            <p className="text-sm font-medium text-emerald-800">
                                                {cronToText(form.recurring_cron)}
                                            </p>
                                            <p className="text-xs text-emerald-500 font-mono mt-0.5">{form.recurring_cron}</p>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* STEP 3: Audience */}
                    {currentStep === 3 && (
                        <div className="space-y-5">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900">Audiência</h2>
                                <p className="mt-1 text-sm text-gray-500">Segmente quem vai receber</p>
                            </div>

                            {/* Tags */}
                            <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-4 space-y-3">
                                <div className="flex items-center justify-between">
                                    <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400">Filtrar por tags</h4>
                                    <button
                                        onClick={() => setShowCreateTag(true)}
                                        className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
                                    >
                                        <Plus className="h-3.5 w-3.5" /> Criar tag
                                    </button>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {domainTags.map((tag) => {
                                        const isSelected = form.segment_tags.includes(tag.name);
                                        return (
                                            <button
                                                key={tag.id}
                                                onClick={() => updateForm({
                                                    segment_tags: isSelected
                                                        ? form.segment_tags.filter(t => t !== tag.name)
                                                        : [...form.segment_tags, tag.name],
                                                })}
                                                className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-all ${
                                                    isSelected ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 text-gray-600 hover:border-gray-400'
                                                }`}
                                            >
                                                <div className="h-2 w-2 rounded-full" style={{ backgroundColor: isSelected ? '#fff' : tag.color }} />
                                                {tag.name}
                                                {isSelected && <Check className="h-3 w-3" />}
                                            </button>
                                        );
                                    })}
                                    {domainTags.length === 0 && <p className="text-sm text-gray-400">Nenhuma tag cadastrada</p>}
                                </div>
                            </div>

                            {/* Geo */}
                            <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-4 space-y-4">
                                <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400">Localização</h4>

                                {/* Países */}
                                <div>
                                    <Label className="text-xs text-gray-500">Países</Label>
                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                        {geoCountries.map((c) => {
                                            const isSelected = form.segment_countries.includes(c.countryCode);
                                            return (
                                                <button
                                                    key={c.countryCode}
                                                    onClick={() => updateForm({
                                                        segment_countries: isSelected
                                                            ? form.segment_countries.filter(x => x !== c.countryCode)
                                                            : [...form.segment_countries, c.countryCode],
                                                        ...(isSelected ? { segment_states: [], segment_cities: [] } : {}),
                                                    })}
                                                    className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-all ${
                                                        isSelected ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 text-gray-600 hover:border-gray-400'
                                                    }`}
                                                >
                                                    {c.country || c.countryCode}
                                                    <span className={`text-[10px] ${isSelected ? 'text-gray-300' : 'text-gray-400'}`}>({c.totalLeads})</span>
                                                    {isSelected && <Check className="h-3 w-3" />}
                                                </button>
                                            );
                                        })}
                                        {geoCountries.length === 0 && <p className="text-sm text-gray-400">Sem dados de geolocalização</p>}
                                    </div>
                                </div>

                                {/* Estados */}
                                {availableStates.length > 0 && (
                                    <div>
                                        <Label className="text-xs text-gray-500">Estados</Label>
                                        <div className="mt-2 flex flex-wrap gap-1.5">
                                            {availableStates.map((s) => {
                                                const isSelected = form.segment_states.includes(s.stateCode);
                                                return (
                                                    <button
                                                        key={s.stateCode}
                                                        onClick={() => updateForm({
                                                            segment_states: isSelected
                                                                ? form.segment_states.filter(x => x !== s.stateCode)
                                                                : [...form.segment_states, s.stateCode],
                                                            ...(isSelected ? { segment_cities: [] } : {}),
                                                        })}
                                                        className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-all ${
                                                            isSelected ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 text-gray-600 hover:border-gray-400'
                                                        }`}
                                                    >
                                                        {s.state || s.stateCode}
                                                        <span className={`text-[10px] ${isSelected ? 'text-gray-300' : 'text-gray-400'}`}>({s.totalLeads})</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Cidades */}
                                {availableCities.length > 0 && (
                                    <div>
                                        <Label className="text-xs text-gray-500">Cidades</Label>
                                        <div className="mt-2 flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                                            {availableCities.map((c) => {
                                                const isSelected = form.segment_cities.includes(c.city);
                                                return (
                                                    <button
                                                        key={c.city}
                                                        onClick={() => updateForm({
                                                            segment_cities: isSelected
                                                                ? form.segment_cities.filter(x => x !== c.city)
                                                                : [...form.segment_cities, c.city],
                                                        })}
                                                        className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-all ${
                                                            isSelected ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 text-gray-600 hover:border-gray-400'
                                                        }`}
                                                    >
                                                        {c.city}
                                                        <span className={`text-[10px] ${isSelected ? 'text-gray-300' : 'text-gray-400'}`}>({c.totalLeads})</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Filtros extras */}
                            <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-4 space-y-3">
                                <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400">Filtros adicionais</h4>
                                <div className="flex items-center justify-between">
                                    <Label className="text-sm text-gray-700">Apenas com nome preenchido</Label>
                                    <Switch checked={form.segment_has_name} onCheckedChange={(v) => updateForm({ segment_has_name: v })} />
                                </div>
                                <div className="flex items-center justify-between">
                                    <Label className="text-sm text-gray-700">Apenas com e-mail preenchido</Label>
                                    <Switch checked={form.segment_has_email} onCheckedChange={(v) => updateForm({ segment_has_email: v })} />
                                </div>
                            </div>
                        </div>
                    )}
                    {/* STEP 4: Tracking (UTM & Tags) */}
                    {currentStep === 4 && (
                        <div className="space-y-5">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900">UTM & Tags</h2>
                                <p className="mt-1 text-sm text-gray-500">Configure parâmetros UTM e tags automáticas</p>
                            </div>

                            <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-4 space-y-4">
                                <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400">Parâmetros UTM</h4>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    <div><Label className="text-xs text-gray-500">utm_source</Label><Input value={form.utm_source} onChange={(e) => updateForm({ utm_source: e.target.value })} className="mt-1 rounded-lg text-sm h-9" /></div>
                                    <div><Label className="text-xs text-gray-500">utm_medium</Label><Input value={form.utm_medium} onChange={(e) => updateForm({ utm_medium: e.target.value })} className="mt-1 rounded-lg text-sm h-9" /></div>
                                    <div><Label className="text-xs text-gray-500">utm_campaign</Label><Input value={form.utm_campaign} onChange={(e) => updateForm({ utm_campaign: e.target.value })} placeholder={form.name || 'nome-campanha'} className="mt-1 rounded-lg text-sm h-9" /></div>
                                </div>
                            </div>

                            <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-4 space-y-4">
                                <div className="flex items-center justify-between">
                                    <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400">Tags automáticas</h4>
                                    <button
                                        onClick={() => setShowCreateTag(true)}
                                        className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
                                    >
                                        <Plus className="h-3.5 w-3.5" /> Criar tag
                                    </button>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs text-gray-500">Tag ao receber</Label>
                                        <Select value={form.tag_on_receive || 'none'} onValueChange={(v) => updateForm({ tag_on_receive: v === 'none' ? '' : v })}>
                                            <SelectTrigger className="mt-1 rounded-lg text-sm h-9"><SelectValue placeholder="Nenhuma" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">Nenhuma</SelectItem>
                                                {domainTags.map(t => (<SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="text-xs text-gray-500">Tag ao clicar</Label>
                                        <Select value={form.tag_on_click || 'none'} onValueChange={(v) => updateForm({ tag_on_click: v === 'none' ? '' : v })}>
                                            <SelectTrigger className="mt-1 rounded-lg text-sm h-9"><SelectValue placeholder="Nenhuma" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">Nenhuma</SelectItem>
                                                {domainTags.map(t => (<SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* STEP 5: Review */}
                    {currentStep === 5 && (
                        <div className="space-y-5">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900">Revisar Campanha</h2>
                                <p className="mt-1 text-sm text-gray-500">Confira todos os detalhes antes de enviar</p>
                            </div>

                            <div className="space-y-3">
                                <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-4 space-y-2">
                                    <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Conteúdo</h4>
                                    <div className="flex justify-between text-sm"><span className="text-gray-500">Nome</span><span className="font-medium text-gray-900">{form.name || '-'}</span></div>
                                    <div className="flex justify-between text-sm"><span className="text-gray-500">Título</span><span className="font-medium text-gray-900">{form.title || '-'}</span></div>
                                    <div className="flex justify-between text-sm"><span className="text-gray-500">URL</span><span className="font-medium text-gray-900 text-right max-w-[200px] truncate">{form.url || '-'}</span></div>
                                </div>
                                <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-4 space-y-2">
                                    <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Envio</h4>
                                    <div className="flex justify-between text-sm"><span className="text-gray-500">Tipo</span><span className="font-medium text-gray-900">{{ direct: 'Envio Direto', scheduled: 'Agendada', recurring: 'Recorrente' }[form.type]}</span></div>
                                    {form.type === 'scheduled' && form.scheduled_at && (
                                        <div className="flex justify-between text-sm"><span className="text-gray-500">Agendado para</span><span className="font-medium text-gray-900">{new Date(form.scheduled_at).toLocaleString('pt-BR')}</span></div>
                                    )}
                                    {form.type === 'recurring' && (
                                        <div className="flex justify-between text-sm"><span className="text-gray-500">Frequência</span><span className="font-medium text-gray-900">{cronToText(form.recurring_cron)}</span></div>
                                    )}
                                </div>
                                <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-4 space-y-2">
                                    <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Segmentação</h4>
                                    <div className="flex justify-between text-sm"><span className="text-gray-500">Público</span><span className="font-medium text-gray-900">{form.segment_tags.length === 0 && form.segment_countries.length === 0 && !form.segment_has_name && !form.segment_has_email ? 'Todos' : 'Segmentado'}</span></div>
                                    {form.segment_tags.length > 0 && <div className="flex justify-between text-sm"><span className="text-gray-500">Tags</span><span className="font-medium text-gray-900">{form.segment_tags.join(', ')}</span></div>}
                                    {form.segment_countries.length > 0 && <div className="flex justify-between text-sm"><span className="text-gray-500">Países</span><span className="font-medium text-gray-900">{form.segment_countries.join(', ')}</span></div>}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Modal criar tag */}
                    {showCreateTag && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowCreateTag(false)}>
                            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center justify-between">
                                    <h3 className="text-sm font-bold text-gray-900">Nova tag</h3>
                                    <button onClick={() => { setShowCreateTag(false); setNewTag({ name: '', color: '#6B7280', description: '' }); }} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
                                </div>
                                <div>
                                    <Label className="text-xs text-gray-500">Nome *</Label>
                                    <Input value={newTag.name} onChange={(e) => setNewTag(p => ({ ...p, name: e.target.value }))} placeholder="ex: vip, promo_marco" className="mt-1 rounded-lg text-sm h-9" autoFocus />
                                </div>
                                <div>
                                    <Label className="text-xs text-gray-500">Cor</Label>
                                    <div className="mt-1.5 flex gap-1.5">
                                        {['#6B7280', '#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#06B6D4'].map((c) => (
                                            <button key={c} onClick={() => setNewTag(p => ({ ...p, color: c }))} className={`h-6 w-6 rounded-full transition-all ${newTag.color === c ? 'ring-2 ring-offset-1 ring-gray-400 scale-110' : 'hover:scale-105'}`} style={{ backgroundColor: c }} />
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-xs text-gray-500">Descrição</Label>
                                    <Input value={newTag.description} onChange={(e) => setNewTag(p => ({ ...p, description: e.target.value }))} placeholder="Opcional" className="mt-1 rounded-lg text-sm h-9" />
                                </div>
                                <Button
                                    className="w-full rounded-lg bg-gray-900 hover:bg-gray-800 text-white"
                                    size="sm"
                                    disabled={!newTag.name.trim() || creatingTag}
                                    onClick={async () => {
                                        if (!form.domain_id || !newTag.name.trim()) return;
                                        setCreatingTag(true);
                                        try {
                                            await createPushTag({ domain_id: form.domain_id, name: newTag.name.trim(), color: newTag.color, description: newTag.description || undefined });
                                            queryClient.invalidateQueries({ queryKey: ['push-tags'] });
                                            toast({ title: 'Tag criada!' });
                                            setShowCreateTag(false);
                                            setNewTag({ name: '', color: '#6B7280', description: '' });
                                        } catch (e: any) {
                                            toast({ variant: 'destructive', title: 'Erro', description: e.message });
                                        } finally {
                                            setCreatingTag(false);
                                        }
                                    }}
                                >
                                    {creatingTag ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Plus className="mr-2 h-3.5 w-3.5" />}
                                    Criar tag
                                </Button>
                            </div>
                        </div>
                    )}
                    {/* Navigation */}
                    <div className="mt-8 flex items-center justify-between">
                        <Button
                            variant="outline"
                            className="rounded-xl gap-2"
                            onClick={() => currentStep > 0 ? setCurrentStep(currentStep - 1) : navigate('/push/campaigns')}
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Voltar
                        </Button>

                        {currentStep < STEPS.length - 1 ? (
                            <Button
                                className="rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white gap-2"
                                disabled={!canAdvance()}
                                onClick={() => setCurrentStep(currentStep + 1)}
                            >
                                Próximo <ArrowRight className="h-4 w-4" />
                            </Button>
                        ) : (
                            <Button
                                className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                                disabled={createMutation.isPending}
                                onClick={handleSubmit}
                            >
                                {createMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                {form.type === 'direct' ? 'Enviar agora' : form.type === 'scheduled' ? 'Agendar campanha' : 'Criar recorrente'}
                            </Button>
                        )}
                    </div>
                </div>

                {/* Preview sidebar */}
                <div className="lg:col-span-1">
                    <div className="sticky top-6 space-y-4">
                        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400">Preview</h3>
                                <div className="flex items-center gap-1 rounded-lg bg-gray-100 p-0.5">
                                    <button
                                        onClick={() => setPreviewMode('mobile')}
                                        className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-all ${
                                            previewMode === 'mobile' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                                        }`}
                                    >
                                        <Smartphone className="h-3 w-3" /> Mobile
                                    </button>
                                    <button
                                        onClick={() => setPreviewMode('desktop')}
                                        className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-all ${
                                            previewMode === 'desktop' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                                        }`}
                                    >
                                        <Monitor className="h-3 w-3" /> Desktop
                                    </button>
                                </div>
                            </div>

                            {previewMode === 'mobile' ? (
                                /* Mobile phone mockup */
                                <div className="mx-auto w-[220px]">
                                    <div className="rounded-[24px] border-[3px] border-gray-800 bg-gray-900 p-1.5 shadow-xl">
                                        <div className="rounded-[18px] bg-gray-100 overflow-hidden">
                                            {/* Status bar */}
                                            <div className="flex items-center justify-between px-4 py-1.5 bg-gray-100">
                                                <span className="text-[10px] font-semibold text-gray-800">
                                                    {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                                </span>
                                                <div className="flex items-center gap-0.5">
                                                    <Battery className="h-3 w-3 text-gray-600" />
                                                </div>
                                            </div>

                                            {/* Notification */}
                                            <div className="px-2.5 pt-2">
                                                <div className="rounded-xl bg-white shadow-sm p-2.5">
                                                    <div className="flex items-center gap-1.5 mb-1.5">
                                                        <div className="h-3 w-3 rounded-full bg-emerald-500 flex items-center justify-center">
                                                            <Globe className="h-1.5 w-1.5 text-white" />
                                                        </div>
                                                        <span className="text-[9px] text-gray-500 font-medium">Chrome · {selectedDomain?.domain || 'site.com'}</span>
                                                        <span className="text-[9px] text-gray-400 ml-auto">agora</span>
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-[11px] font-bold text-gray-900 truncate">{form.title || 'Título da notificação'}</p>
                                                            <p className="text-[10px] text-gray-500 line-clamp-2 mt-0.5">{form.body || 'Mensagem da notificação...'}</p>
                                                        </div>
                                                        {form.icon && (
                                                            <img src={form.icon} alt="icon" className="h-8 w-8 rounded-lg object-cover shrink-0" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                                        )}
                                                    </div>
                                                    {form.image && (
                                                        <img src={form.image} alt="banner" className="w-full h-20 rounded-lg object-cover mt-2" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                                    )}
                                                </div>
                                            </div>

                                            {/* Spacer */}
                                            <div className="h-48" />

                                            {/* Home indicator */}
                                            <div className="flex justify-center pb-2 pt-1">
                                                <div className="h-1 w-16 rounded-full bg-gray-800" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                /* Desktop preview */
                                <div className="rounded-xl border border-gray-200 overflow-hidden">
                                    {form.image && (
                                        <img src={form.image} alt="banner" className="w-full h-28 object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                    )}
                                    <div className="p-3">
                                        <div className="flex gap-2.5">
                                            {form.icon && <img src={form.icon} alt="icon" className="h-9 w-9 rounded-lg object-cover shrink-0" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />}
                                            <div className="min-w-0">
                                                <p className="text-sm font-semibold text-gray-900 truncate">{form.title || 'Título da notificação'}</p>
                                                <p className="mt-0.5 text-xs text-gray-500 line-clamp-2">{form.body || 'Mensagem da notificação...'}</p>
                                            </div>
                                        </div>
                                        {form.url && (
                                            <div className="mt-2 flex items-center gap-1 text-[11px] text-blue-500">
                                                <ExternalLink className="h-3 w-3" />
                                                <span className="truncate">{form.url}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Summary */}
                        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">Resumo</h3>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between"><span className="text-gray-500">Domínio</span><span className="font-medium text-gray-900">{configuredDomains.find(d => d.id === form.domain_id)?.domain || '-'}</span></div>
                                {form.name && <div className="flex justify-between"><span className="text-gray-500">Nome</span><span className="font-medium text-gray-900">{form.name}</span></div>}
                                <div className="flex justify-between"><span className="text-gray-500">Tipo</span><span className="font-medium text-gray-900">{{ direct: 'Envio Direto', scheduled: 'Agendada', recurring: 'Recorrente' }[form.type]}</span></div>
                                <div className="flex justify-between"><span className="text-gray-500">Público</span><span className="font-medium text-gray-900">{form.segment_tags.length === 0 && form.segment_countries.length === 0 && !form.segment_has_name && !form.segment_has_email ? 'Todos' : 'Segmentado'}</span></div>
                                {form.type === 'scheduled' && form.scheduled_at && (
                                    <div className="flex justify-between"><span className="text-gray-500">Envio em</span><span className="font-medium text-gray-900">{new Date(form.scheduled_at).toLocaleString('pt-BR')}</span></div>
                                )}
                                {form.type === 'recurring' && (
                                    <div className="flex justify-between"><span className="text-gray-500">Frequência</span><span className="font-medium text-gray-900 text-right max-w-[140px]">{cronToText(form.recurring_cron)}</span></div>
                                )}
                                {form.segment_tags.length > 0 && (
                                    <div className="flex justify-between"><span className="text-gray-500">Tags</span><span className="font-medium text-gray-900">{form.segment_tags.join(', ')}</span></div>
                                )}
                                {form.segment_countries.length > 0 && (
                                    <div className="flex justify-between"><span className="text-gray-500">Países</span><span className="font-medium text-gray-900">{form.segment_countries.join(', ')}</span></div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}